/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, September 17th 2022, 1:18:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useNavigate } from "react-router";
import { isEmpty } from "lodash";

import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import StorageIcon from "@mui/icons-material/Storage";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { resolveURLName } from "core/routes/utils";
import { useDatasetSchemaAPI } from "datasets/services/DatasetSchemaAPI";
import { DatasetSchema } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { AsyncAPIListAutoComplete, HelperText } from "ui-widgets";

interface DatasetSchemaPickerProps {
  datasetSchemaId: DatasetSchema["id"];
  handleChooseDatasetSchema: (datasetSchema: DatasetSchema) => void;
  datasetSchemaTag?: DatasetSchema["artifact_guid"];
  onLoadInitial?: (datasetSchema: DatasetSchema) => void;
  label?: string;
  helpText?: string;
}

export const DatasetSchemaPicker = ({
  label = "Dataset",
  datasetSchemaId,
  datasetSchemaTag = null,
  handleChooseDatasetSchema,
  onLoadInitial,
  helpText,
  ...props
}: DatasetSchemaPickerProps) => {
  // // This valid check ensures a value is never undefined unless it is a hyphen so it can be resolved in AsyncAPIListAutoComplete
  // // TODO: have the API return a null value instead of this hyphen.
  const validDatasetSchemaTag = !datasetSchemaId ? datasetSchemaTag !== "-" && datasetSchemaTag : null;

  const { useAPIFetchDatasetSchema } = useDatasetSchemaAPI();

  const { apiItem, isMakingAPIRequest, fetchItem } = useAPIFetchDatasetSchema(
    validDatasetSchemaTag && { artifact_guid: validDatasetSchemaTag }
  );

  const fetchedDatasetSchema = validDatasetSchemaTag ? apiItem?.[0] ?? {} : apiItem;

  React.useEffect(() => {
    if (datasetSchemaId) {
      fetchItem(datasetSchemaId);
      return;
    }

    if (validDatasetSchemaTag) fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetSchemaId, validDatasetSchemaTag]);

  React.useEffect(() => {
    !isEmpty(fetchedDatasetSchema) && onLoadInitial?.(fetchedDatasetSchema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fetchedDatasetSchema)]);

  const navigate = useNavigate();

  const navigateToDatasetList = () => navigate(resolveURLName("databanks"));
  const navigateToDataset = () =>
    navigate(
      resolveURLName("editDataset", {
        datasetSchemaId: fetchedDatasetSchema?.id,
        dataBankId: fetchedDatasetSchema?.data_bank,
      })
    );

  return (
    <Stack>
      <AsyncAPIListAutoComplete<DatasetSchema>
        {...props}
        id="dataset-select-dropdown"
        tooltipProps={{ title: "" }}
        label={label}
        itemIdProperty="id"
        getOptionLabel={(option) => option.json_schema?.title}
        loadingText="Loading Datasets"
        onChangeCallBack={handleChooseDatasetSchema}
        value={fetchedDatasetSchema}
        apiServicePath={APISERVICEPATHS.datasetSchemas}
        inputStartAdornment={isMakingAPIRequest ? <CircularProgress /> : <StorageIcon color="primary" />}
        inputEndAdornment={
          fetchedDatasetSchema?.id && (
            <Tooltip title="Open Dataset">
              <IconButton onClick={navigateToDataset}>
                <OpenInFullIcon color="primary" />
              </IconButton>
            </Tooltip>
          )
        }
        fullWidth
        noOptionsText={
          <Button variant="text" onClick={navigateToDatasetList}>
            No datasets exist for your organization. Click here to add datasets to your organization's databanks.
          </Button>
        }
      />
      {helpText && <HelperText>{helpText}</HelperText>}
    </Stack>
  );
};
